import { Expediente, ProcesoExpediente, ResultadoExpediente } from "../types/expediente";

/**
 * Del expediente de UCampus a las dos vistas que sirven para trabajar: una fila
 * por estudiante (cuánto le falta) y una fila por trámite (a cuántos les falta
 * ese). Es el único lugar donde se decide cómo se cuenta.
 *
 * La unidad es el expediente, no la persona: licenciatura y título son
 * expedientes distintos y avanzan por separado, así que quien tenga los dos
 * aparece dos veces, cada una con su carrera.
 */

export interface FilaEstudiante {
  clave: string;
  rut: number;
  nombre: string;
  carrCodigo: number | null;
  carrera: string;
  /** La carrera se muestra solo si esta persona tiene más de un expediente. */
  variasCarreras: boolean;
  estado: string;
  nota: string | null;
  completos: number;
  total: number;
  procesos: ProcesoExpediente[];
  pendientes: ProcesoExpediente[];
  /** El primer trámite pendiente: es lo que hay que ir a destrabar. */
  siguiente: ProcesoExpediente | null;
  consultadoEn: string | null;
  error: string | null;
}

export interface ResumenProceso {
  slug: string;
  nombre: string;
  grupo: string;
  orden: number;
  completos: number;
  /** Quiénes lo tienen pendiente: sin esto el conteo no dice a quién ir a buscar. */
  pendientes: FilaEstudiante[];
}

export interface GrupoResumen {
  grupo: string;
  procesos: ResumenProceso[];
}

/** Los expedientes de una carrera: licenciatura y título se miran por separado. */
export interface GrupoCarrera {
  clave: string;
  carrera: string;
  filas: FilaEstudiante[];
  secciones: GrupoResumen[];
}

/** Un estudiante con su(s) expediente(s): el nombre arriba, lo que le falta abajo. */
export interface FichaEstudiante {
  rut: number;
  nombre: string;
  expedientes: FilaEstudiante[];
}

function fila(resultado: ResultadoExpediente, expediente: Expediente, variasCarreras: boolean): FilaEstudiante {
  const pendientes = expediente.procesos.filter((p) => !p.completo);
  return {
    clave: `${resultado.rut}-${expediente.carr_codigo ?? "?"}`,
    rut: resultado.rut,
    nombre: resultado.nombre || String(resultado.rut),
    carrCodigo: expediente.carr_codigo,
    carrera: expediente.carrera,
    variasCarreras,
    estado: expediente.estado,
    nota: expediente.nota,
    completos: expediente.completos,
    total: expediente.total,
    procesos: expediente.procesos,
    pendientes,
    siguiente: pendientes[0] ?? null,
    consultadoEn: resultado.consultado_en,
    error: null,
  };
}

function filaConError(resultado: ResultadoExpediente): FilaEstudiante {
  return {
    clave: String(resultado.rut),
    rut: resultado.rut,
    nombre: resultado.nombre || String(resultado.rut),
    carrCodigo: null,
    carrera: "",
    variasCarreras: false,
    estado: "",
    nota: null,
    completos: 0,
    total: 0,
    procesos: [],
    pendientes: [],
    siguiente: null,
    consultadoEn: resultado.consultado_en,
    error: resultado.error ?? "UCampus no devolvió expediente.",
  };
}

/** Una fila por expediente; las consultas que fallaron quedan como fila con `error`. */
export function filasDeEstudiantes(resultados: ResultadoExpediente[]): FilaEstudiante[] {
  const filas: FilaEstudiante[] = [];
  for (const resultado of resultados) {
    if (resultado.error || resultado.expedientes.length === 0) {
      filas.push(filaConError(resultado));
      continue;
    }
    const varias = resultado.expedientes.length > 1;
    for (const expediente of resultado.expedientes) filas.push(fila(resultado, expediente, varias));
  }
  return filas;
}

/**
 * El mismo trámite mirado en todos: cuántos lo tienen completo y quiénes no.
 *
 * Los trámites se juntan por el `slug` de UCampus (no por el nombre, que cambia
 * de redacción entre carreras) y se ordenan como los muestra el expediente:
 * por sección y, dentro de ella, por su posición en la tabla.
 */
export function resumenPorProceso(filas: FilaEstudiante[]): GrupoResumen[] {
  const procesos = new Map<string, ResumenProceso>();

  for (const f of filas) {
    if (f.error) continue;
    for (const p of f.procesos) {
      let resumen = procesos.get(p.slug);
      if (!resumen) {
        resumen = { slug: p.slug, nombre: p.nombre, grupo: p.grupo, orden: p.orden, completos: 0, pendientes: [] };
        procesos.set(p.slug, resumen);
      }
      resumen.orden = Math.min(resumen.orden, p.orden);
      if (p.completo) resumen.completos += 1;
      else resumen.pendientes.push(f);
    }
  }

  const grupos = new Map<string, ResumenProceso[]>();
  for (const resumen of [...procesos.values()].sort((a, b) => a.orden - b.orden)) {
    if (!grupos.has(resumen.grupo)) grupos.set(resumen.grupo, []);
    grupos.get(resumen.grupo)!.push(resumen);
  }
  return [...grupos.entries()].map(([grupo, procesos]) => ({ grupo, procesos }));
}

/** El nombre de la carrera sin la versión del plan ("… mención Computación v5"). */
export function nombreCarrera(carrera: string): string {
  return carrera.replace(/\s+v\d+\s*$/i, "").trim();
}

/**
 * Los expedientes separados por carrera.
 *
 * La licenciatura y el título son trámites distintos, con su propia tabla de
 * procesos y su propio avance: contarlos juntos daría un "8 pendientes" que no
 * es de nadie. Se ordenan por `carr_codigo` (la licenciatura antes que el
 * título) y las consultas que fallaron quedan fuera —no tienen carrera— porque
 * se listan aparte, con su error.
 */
export function porCarrera(filas: FilaEstudiante[]): GrupoCarrera[] {
  const carreras = new Map<string, { orden: number; carrera: string; filas: FilaEstudiante[] }>();

  for (const f of filas) {
    if (f.error) continue;
    const clave = String(f.carrCodigo ?? f.carrera);
    if (!carreras.has(clave)) {
      carreras.set(clave, { orden: f.carrCodigo ?? Number.MAX_SAFE_INTEGER, carrera: nombreCarrera(f.carrera), filas: [] });
    }
    carreras.get(clave)!.filas.push(f);
  }

  return [...carreras.entries()]
    .sort((a, b) => a[1].orden - b[1].orden)
    .map(([clave, { carrera, filas }]) => ({ clave, carrera, filas, secciones: resumenPorProceso(filas) }));
}

/**
 * Deja solo los trámites que tienen a alguien pendiente y descarta las secciones
 * que quedan vacías.
 *
 * Una sección donde nadie debe nada —"Requisitos Académicos" cuando todos los
 * aprobaron— no tiene nada que decir y solo estira la lista: lo que se busca acá
 * es a quién ir a buscar.
 */
export function soloConPendientes(secciones: GrupoResumen[]): GrupoResumen[] {
  return secciones
    .map(({ grupo, procesos }) => ({ grupo, procesos: procesos.filter((p) => p.pendientes.length > 0) }))
    .filter(({ procesos }) => procesos.length > 0);
}

/** Si a alguien le falta algo en esta carrera. */
export function hayPendientes(secciones: GrupoResumen[]): boolean {
  return secciones.some((s) => s.procesos.some((p) => p.pendientes.length > 0));
}

/**
 * Los expedientes agrupados por persona, en el orden en que se consultaron.
 *
 * Quien tiene dos carreras aparece una sola vez, con un expediente debajo del
 * otro: la pregunta que se responde acá es "a esta persona qué le falta", no
 * "cuántos expedientes hay".
 */
export function porEstudiante(filas: FilaEstudiante[]): FichaEstudiante[] {
  const fichas = new Map<number, FichaEstudiante>();
  for (const f of filas) {
    let ficha = fichas.get(f.rut);
    if (!ficha) {
      ficha = { rut: f.rut, nombre: f.nombre, expedientes: [] };
      fichas.set(f.rut, ficha);
    }
    ficha.expedientes.push(f);
  }
  return [...fichas.values()];
}
